import { useCallback, useEffect, useRef, useState } from 'react';
import { GAME_RULES, echoLengths } from '@continuum/show-core';
import type { EchoTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';
import { DishSprite } from './art.js';

const PAD_COLOURS = ['#4fd8c2', '#6fb7ff', '#ff4fd8', '#ffd94f'];

/**
 * SIGNAL OFFICER — a pattern arrives, you tap it back.
 *
 * Simon, in morse clothing, in three rounds: each signal one light longer and played faster
 * than the last. A wrong pad replays that round, not the whole game. The signal could arrive
 * damaged (one step as static); no era asks for that any more.
 */
export function Echo({ task, era, onProgress, onWin, onMistake, accent, onFrame, paused = false, s }: GameProps<EchoTask>) {
  const { padCount, gapMs, leadMs, retryMs, roundSpeeds, roundPauseMs } = GAME_RULES.echo;
  // Three rounds, each a light longer and played faster (Marek, 2026-09-29).
  const [patterns] = useState(() =>
    echoLengths(task).map((len) => Array.from({ length: len }, () => Math.floor(Math.random() * padCount))),
  );
  const rounds = patterns.length;
  const total = patterns.reduce((n, p) => n + p.length, 0);
  const [round, setRound] = useState(0);
  const roundRef = useRef(0);
  roundRef.current = round;
  const pattern = patterns[round] ?? patterns[0]!;
  /** Which step arrives as static, if the signal is damaged. */
  const [corruptAt] = useState(() =>
    task.corrupted ? 1 + Math.floor(Math.random() * Math.max(1, task.length - 1)) : -1,
  );
  const [lit, setLit] = useState<number | null>(null);
  /** The damaged step is playing: every pad goes grey at once, so none of them gives it away. */
  const [crackle, setCrackle] = useState(false);
  const [phase, setPhase] = useState<'playing' | 'listening' | 'wrong' | 'next'>('playing');
  const [entered, setEntered] = useState(0);
  const won = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };
  const hold = useRef(paused);
  hold.current = paused;
  /** Each playback has a number; covering the board cancels the one running. */
  const run = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const playBack = useCallback(() => {
    const mine = ++run.current;
    const r = roundRef.current;
    const steps = patterns[r] ?? patterns[0]!;
    const speed = roundSpeeds[r] ?? 1;
    setPhase('playing');
    setEntered(0);
    let step = 0;
    const next = () => {
      if (mine !== run.current) return;
      if (step >= steps.length) {
        setLit(null);
        setPhase('listening');
        return;
      }
      const damaged = step === corruptAt;
      if (damaged) setCrackle(true);
      else setLit(steps[step]!);
      setTimeout(() => {
        if (mine !== run.current) return;
        setLit(null);
        setCrackle(false);
        step += 1;
        setTimeout(next, gapMs * speed);
      }, task.playbackMs * speed);
    };
    setTimeout(next, leadMs);
  }, [patterns, corruptAt, task.playbackMs, gapMs, leadMs, roundSpeeds]);

  useEffect(() => {
    if (!hold.current) playBack();
  }, [playBack]);

  // Covered mid-signal, the guest would miss it: stop it, and play it again from the start
  // when the board is back.
  const missed = useRef(false);
  useEffect(() => {
    if (paused) {
      if (phaseRef.current !== 'listening') {
        missed.current = true;
        run.current += 1;
        setLit(null);
        setCrackle(false);
      }
    } else if (missed.current) {
      missed.current = false;
      playBack();
    }
  }, [paused, playBack]);

  // Which pad is lit, so a watcher sees the same flashes.
  useEffect(() => {
    cbs.current.onFrame(`l:${lit ?? -1}|e:${entered}|r:${round}`);
  }, [lit, entered, round]);

  const doneBefore = patterns.slice(0, round).reduce((n, p) => n + p.length, 0);

  const tap = (pad: number) => {
    if (phase !== 'listening' || hold.current) return;
    if (pattern[entered] === pad) {
      const next = entered + 1;
      setEntered(next);
      cbs.current.onProgress(Math.round(((doneBefore + next) / total) * 100));
      if (next < pattern.length) return;
      if (round + 1 >= rounds) {
        if (!won.current) {
          won.current = true;
          cbs.current.onWin();
        }
        return;
      }
      // The round is done: a moment to breathe, then the next signal, longer and faster.
      // The round moves on at once, so a cover over the board in the pause replays the new one.
      roundRef.current = round + 1;
      setRound(round + 1);
      setEntered(0);
      setPhase('next');
      const mine = ++run.current;
      setTimeout(() => {
        if (mine !== run.current) return;
        if (hold.current) missed.current = true;
        else playBack();
      }, roundPauseMs);
      return;
    }
    // Wrong: a mistake, and this round's pattern plays again.
    cbs.current.onMistake?.();
    setPhase('wrong');
    setTimeout(() => {
      if (hold.current) missed.current = true;
      else playBack();
    }, retryMs);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          textAlign: 'center',
          fontFamily: mono,
          fontSize: 15,
          fontWeight: 700,
          letterSpacing: 1.5,
          color: phase === 'wrong' ? '#ff6b78' : phase === 'next' ? '#ffffff' : accent,
          minHeight: 20,
        }}
      >
        {phase === 'next'
          ? s.echoNextRound(round + 1)
          : phase === 'playing'
            ? crackle
              ? s.echoGrey
              : s.echoWatch
            : phase === 'wrong'
              ? s.echoWrong
              : s.echoYourTurn}
      </div>
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'center', gap: 8 }}>
        {patterns.map((_, r) => (
          <span
            key={r}
            style={{
              fontFamily: mono,
              fontSize: 12,
              letterSpacing: 1.2,
              padding: '3px 9px',
              borderRadius: 999,
              border: `1px solid ${r === round ? accent : `${accent}44`}`,
              color: r < round ? '#05070a' : r === round ? accent : '#6f808c',
              background: r < round ? accent : 'transparent',
            }}
          >
            {s.echoRound(r + 1, rounds)}
          </span>
        ))}
      </div>

      {/* the transmission, as a morse-ish trace */}
      <div style={{ margin: '10px auto 0', display: 'flex', justifyContent: 'center', gap: 6 }}>
        {pattern.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === corruptAt ? 20 : 12,
              height: 4,
              borderRadius: 2,
              background: i < entered ? accent : 'rgba(255,255,255,.16)',
              opacity: i === corruptAt ? 0.45 : 1,
              transition: 'background .2s',
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {Array.from({ length: padCount }, (_, i) => {
          const on = lit === i;
          const colour = crackle ? '#8a8f98' : (PAD_COLOURS[i] ?? accent);
          return (
            <button
              key={i}
              onPointerDown={() => tap(i)}
              disabled={phase !== 'listening'}
              aria-label={`${i + 1}`}
              style={{
                position: 'relative',
                height: 84,
                borderRadius: 14,
                border: `1.5px solid ${on || crackle ? colour : `${colour}55`}`,
                background: on ? `${colour}44` : crackle ? `${colour}30` : `${colour}12`,
                boxShadow: on ? `0 0 34px ${colour}` : 'none',
                transition: 'background .1s, box-shadow .1s',
                touchAction: 'none',
                display: 'grid',
                placeItems: 'center',
                padding: 0,
              }}
            >
              {/* A dish per pad, so four identical squares become four antennae. */}
              <span style={{ width: 46, height: 46, display: 'block', color: colour }}>
                <DishSprite era={era} lit={on} tint={colour} />
              </span>
            </button>
          );
        })}
      </div>

      {corruptAt >= 0 && (
        <div
          style={{
            marginTop: 10,
            textAlign: 'center',
            fontFamily: mono,
            fontSize: 12,
            letterSpacing: 1,
            color: '#ff9a4a',
          }}
        >
          {s.echoGreyWas(corruptAt + 1)}
        </div>
      )}
    </div>
  );
}
